/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   10/08/2022
 */

import React from "react";
import {
	Box,
	Button, Dialog, DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Stack,
} from "@mui/material";
import {useSelector} from "react-redux";
import {State} from "../../state/store";
import {useMutation, useQuery} from "urql";
import {
	EventSubscriptionsQuery,
	EventSubscriptionsQueryVars,
	EventSubscriptionsResponse
} from "../../graphql/queries/event-subscriptions";
import HeroMessage from "../../components/HeroMessage";
import LoadingHero from "../../components/LoadingHero";
import NoSubscriptions from "./NoSubscriptions";
import {Link, useNavigate} from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import {
	DataGrid, GridActionsCellItem, GridRowParams, GridValueFormatterParams, GridValueGetterParams,
} from "@mui/x-data-grid";
import {EmployeesFilter, EventsFilter, EventSubscription} from "../../interfaces/Global";
import {typeLabels} from "./ConfigWhen";
import {
	DeleteEventSubscriptionMutation,
	DeleteEventSubscriptionResponse, DeleteEventSubscriptionVars,
	UpdateEventSubscriptionMutation,
	UpdateEventSubscriptionResponse,
	UpdateEventSubscriptionVars
} from "../../graphql/mutations/event-subscriptions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {LoadingButton} from "@mui/lab";
import {dateTimeCol} from "../../dates-helper";

export default function SubscriptionsList() {

	const navigate = useNavigate();
	const user = useSelector((s:State) => s.user);

	const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = React.useState(false);
	const [deleteItemId, setDeleteItemId] = React.useState(0);

	const [ eventSubscriptionsResult ] = useQuery<EventSubscriptionsResponse, EventSubscriptionsQueryVars>({
		query: EventSubscriptionsQuery,
		requestPolicy: "cache-and-network",
		variables: {
			createdBy: user?.id,
			orderBy: "id"
		}
	});

	const [, updateEventSubscription] = useMutation<UpdateEventSubscriptionResponse, UpdateEventSubscriptionVars>(UpdateEventSubscriptionMutation);
	const [deleteResult, deleteEventSubscription] = useMutation<DeleteEventSubscriptionResponse, DeleteEventSubscriptionVars>(DeleteEventSubscriptionMutation);

	const eventSubscriptions = eventSubscriptionsResult.data?.eventSubscriptions.items || [];

	return (
		<Stack>
			{eventSubscriptionsResult.error &&
				<HeroMessage
					p={4}
					title={"Request Error"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={eventSubscriptionsResult.error.message}
				/>
			}
			{eventSubscriptionsResult.fetching &&
				<LoadingHero />
			}
			{!eventSubscriptionsResult.error && !eventSubscriptionsResult.fetching && eventSubscriptions.length === 0 &&
				<NoSubscriptions />
			}
			{!eventSubscriptionsResult.error && !eventSubscriptionsResult.fetching && eventSubscriptions.length > 0 &&
				<>
					<Box sx={{ mb: 1 }}>
						<Button
							variant={"outlined"}
							color={"primary"}
							component={Link}
							to={"/notification/events/new"}
							startIcon={<AddIcon />}
						>
							New Event Notification
						</Button>
					</Box>
					<DataGrid
						rows={eventSubscriptions}
						autoHeight
						hideFooterSelectedRowCount
						loading={eventSubscriptionsResult.fetching}
						density={"compact"}
						disableColumnMenu
						columns={[
							{ field: "id", headerName: "ID", width: 70 },
							{ field: "creationDate", ...dateTimeCol, headerName: "Creation Date" },
							{
								field: "channel",
								valueGetter: (params: GridValueGetterParams<any, EventSubscription>) => {
									return params.row?.config?.channel?.name ?? null;
								},
								valueFormatter: (params: GridValueFormatterParams<string|null>) => {
									if (!params.value) return "Myself";
									const channel = eventSubscriptions.find(i => i.config?.channel?.name === params.value)?.config?.channel;
									if (!channel) return null;
									return (channel.isPrivate ? "🔒" : "#") + params.value;
								},
								headerName: "Sent To",
								flex: 1
							}, {
								field: "when",
								valueGetter: (params: GridValueGetterParams<any, EventSubscription>) => {
									return params.row?.config?.types ?? null;
								},
								valueFormatter: (params: GridValueFormatterParams<string[]|null>) => {
									return params.value?.map(type => typeLabels[type]).join(", ");
								},
								headerName: "When",
								flex: 1
							}, {
								field: "employeesFilter",
								valueGetter: (params: GridValueGetterParams<any, EventSubscription>) => {
									return params.row?.config?.employeesFilter;
								},
								valueFormatter: (params: GridValueFormatterParams<EmployeesFilter>) => {
									if (!params.value || params.value.length === 0) {
										return null;
									}
									let description = "";
									params.value.forEach((f, index) => {
										const [op, attr, values] = f;
										const opDesc = (op === "-" ? " excluding ":(index !== 0 ? " or " : ""));
										if (attr === "managerId") {
											description += opDesc + " Direct Reports of: " + values.join(", ");
										} if (attr === "id") {
											description += opDesc + " Employee ID: " + values.join(", ");
										} else if (attr === "countryCode") {
											description += opDesc + " Country: " + values.join(", ");
										} else if (attr === "company") {
											description += opDesc + " Product: " + values.join(", ");
										} else if (attr === "location") {
											description += opDesc + " Location: " + values.join(", ");
										} else if (attr === "department") {
											description += opDesc + " Department: " + values.join(", ");
										} else if (attr === "division") {
											description += opDesc + " Division: " + values.join(", ");
										} else if (attr === "tags") {
											description += opDesc + " Custom tags: " + values.join(", ");
										}
									});
									return description;
								},
								headerName: "Employees Filter",
								flex: 1
							}, {
								field: "eventsFilter",
								valueGetter: (params: GridValueGetterParams<any, EventSubscription>) => {
									return params.row?.config?.eventsFilter;
								},
								valueFormatter: (params: GridValueFormatterParams<EventsFilter>) => {
									if (!params.value || params.value.length === 0) {
										return null;
									}
									let description: string[] = [];
									params.value.forEach((f) => {
										const [, attr, values] = f;
										if (attr === "typeId") {
											description.push("Type: " + values.join(", "));
										} if (attr === "categoryId") {
											description.push("Category: " + values.join(", "));
										} else if (attr === "isConfirmed") {
											description.push(values[0] ? "Confirmed" : "Not Confirmed");
										}
									});
									return description.join(" and ");
								},
								headerName: "Events Filter",
								flex: 1
							}, {
								field: "triggerCount",
								headerName: "Trigger Count",
								headerAlign: "center",
								align: "center",
								minWidth: 120,
							}, {
								field: "lastTriggered",
								...dateTimeCol,
								headerName: "Last Triggered"
							}, {
								field: "enabled",
								headerName: "Enabled",
								headerAlign: "center",
								align: "center",
								valueFormatter: (params: GridValueFormatterParams<EventsFilter>) => {
									return params.value ? "Yes" : "No";
								},
							}, {
								field: "actions",
								type: "actions",
								width: 50,
								getActions: (params: GridRowParams<EventSubscription>) => [
									<GridActionsCellItem
										icon={<EditIcon fontSize={"small"} />}
										label="Edit"
										onClick={() => {
											navigate("/notification/events/" + params.row.id);
										}}
										showInMenu
									/>,
									<GridActionsCellItem
										icon={<PowerSettingsNewIcon fontSize={"small"} />}
										label={params.row.enabled ? "Disable" : "Enable"}
										onClick={() => {
											updateEventSubscription({ id: params.row.id, data: { enabled: !params.row.enabled }}).then();
										}}
										showInMenu
									/>,
									<GridActionsCellItem
										icon={<DeleteIcon fontSize={"small"} />}
										label="Delete"
										onClick={() => {
											setDeleteItemId(params.row.id);
											setConfirmDeleteDialogOpen(true);
										}}
										showInMenu
									/>,
								],
							}
						]}
					/>
					<Dialog
						open={confirmDeleteDialogOpen}
						onClose={() => { setConfirmDeleteDialogOpen(false)}}
					>
						<DialogTitle>
							Confirm delete
						</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Are you sure you want to delete this notification? This operation is irreversible.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
							<LoadingButton
								loading={deleteResult.fetching}
								onClick={() => {
									setConfirmDeleteDialogOpen(false);
									deleteEventSubscription({id: deleteItemId}).then(() => setDeleteItemId(0));
								}}
								color={"primary"}
								variant={"contained"}
							>
								Delete
							</LoadingButton>
						</DialogActions>
					</Dialog>
				</>
			}
		</Stack>
	);

}